import { Stack, useTheme } from "@mui/material";
import type {
  GridColDef,
  GridFilterModel,
  GridFilterOperator,
  GridRowModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { DataGridPremium, gridRowSelectionIdsSelector } from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import {
  getRowSelectionModelFromNumericIds,
  getSelectedNumericRowIdsFromModel,
} from "@entrilia/shared/components/grid/gridHelper.ts";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import type { DimensionDescriptor, DimensionDictionary } from "../../../../../../api/types/svTypes.ts";
import { getNewSelection } from "../../../sv/components/viewer/common/conditions/getNewSelection.ts";

interface Props {
  dimension: DimensionDescriptor;
  values: DimensionDictionary[];
  loading: boolean;
  selectedValues: string[];
  showOnlySelected: boolean;
  textFilter: string;
  onSelectionChange: (values: string[]) => void;
}

const DimensionValueGrid = ({
  dimension,
  values,
  loading,
  selectedValues,
  showOnlySelected,
  textFilter,
  onSelectionChange,
}: Props) => {
  const theme = useTheme();

  const columns = useMemo<GridColDef[]>(
    () =>
      dimension.availableFields.map((field, index) => ({
        field,
        disableColumnMenu: true,
        flex: 1,
        disableReorder: true,
        valueFormatter: (value: unknown) => value ?? (index === 0 ? "No Value" : value),
        ...(index === 0 ? { filterOperators: customFilterOperators, filterable: false } : {}),
      })),
    [dimension.availableFields]
  );

  const rowsSelection = useMemo<GridRowSelectionModel>(() => {
    const rowNumbers = values
      .filter((v) => selectedValues.includes(v[dimension.keyFieldName] as string))
      .map((row) => row["id"] as number)
      .filter((v): v is number => v !== undefined);
    return getRowSelectionModelFromNumericIds(rowNumbers);
  }, [values, selectedValues, dimension.keyFieldName]);

  const sx = useMemo(
    () => ({
      "--DataGrid-rowBorderColor": theme.palette.divider,
      border: "1px solid " + theme.palette.divider,
      ".MuiDataGrid-columnHeaders": { borderBottom: "none" },
      ".MuiDataGrid-cell:focus-within": { outline: "unset" },
      ".MuiDataGrid-columnHeader:focus-within": { outline: "unset" },
      ".MuiDataGrid-row .MuiDataGrid-cell": { borderTop: "none", "&:hover": { cursor: "pointer" } },
      ".MuiButtonBase-root.MuiCheckbox-root": { p: 0.75 },
      ".MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--borderTop": { border: "none" },
    }),
    [theme.palette.divider]
  );

  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      const rowNumbers = getSelectedNumericRowIdsFromModel(selectionModel);
      const newSelection = getNewSelection(rowNumbers, values, dimension.keyFieldName);
      onSelectionChange(newSelection);
    },
    [values, dimension.keyFieldName, onSelectionChange]
  );

  const filterModel = useMemo<GridFilterModel>(
    () => ({
      items: [
        {
          field: columns[0]?.field ?? "",
          operator: "dimensionValueFilter",
          value: { showOnlySelected, textFilter, availableFields: dimension.availableFields },
        },
      ],
    }),
    [columns, showOnlySelected, textFilter, dimension.availableFields]
  );

  return (
    <Stack spacing={0.5} sx={{ flex: 1 }}>
      <ScrollableFlexContainer sx={{ minHeight: "255px" }}>
        <DataGridPremium<DimensionDictionary>
          rows={values}
          sx={sx}
          initialState={{
            sorting: { sortModel: [{ field: dimension.keyFieldName, sort: "asc" }] },
          }}
          rowHeight={24}
          checkboxSelection
          rowSelectionModel={rowsSelection}
          keepNonExistentRowsSelected
          onRowSelectionModelChange={handleSelectionChange}
          hideFooter
          disableColumnMenu
          columnHeaderHeight={30}
          filterModel={filterModel}
          disableColumnFilter
          columns={columns}
          loading={loading}
          slots={{ loadingOverlay: InlineLoader }}
        />
      </ScrollableFlexContainer>
    </Stack>
  );
};

export default DimensionValueGrid;

const customFilterOperators: GridFilterOperator[] = [
  {
    label: "Dimension Value Filter",
    value: "dimensionValueFilter",
    getApplyFilterFn: (filterItem) => {
      const { showOnlySelected, textFilter, availableFields } = (filterItem.value ?? {
        availableFields: [],
      }) as {
        showOnlySelected?: boolean;
        textFilter?: string;
        availableFields: string[];
      };
      return (_value, row: GridRowModel<DimensionDictionary>, _column, api) => {
        const matchesSearch =
          !textFilter ||
          Object.entries(row)
            .filter(([key]) => availableFields.includes(key))
            .some(([, value]) =>
              String(value ?? "No Value")
                .toLowerCase()
                .includes(textFilter.toLowerCase())
            );

        const matchesSelected =
          !showOnlySelected ||
          (Array.from(gridRowSelectionIdsSelector(api).keys()) as number[]).includes(row["id"] as number);

        return matchesSearch && matchesSelected;
      };
    },
  },
];
